import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Grid, Card, CardContent, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Alert, IconButton, Tooltip, LinearProgress, Tabs, Tab, Paper, Divider,
  Table, TableHead, TableRow, TableCell, } from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import EventIcon from '@mui/icons-material/Event';
import DownloadIcon from '@mui/icons-material/Download';
import VerifiedIcon from '@mui/icons-material/Verified';
import {
  ASTROLOGY_CONSULTANTS_QUERY, MY_ASTROLOGY_CONSULTATIONS_QUERY, MY_CEREMONIES_QUERY,
  CREATE_ASTROLOGY_CONSULTATION_MUTATION, UPDATE_ASTROLOGY_CONSULTATION_MUTATION, DELETE_ASTROLOGY_CONSULTATION_MUTATION,
} from '../graphql/queries';

import Paged from '../components/Paged'; // lists beyond 10 are paged
const STATUS_COLOR = { scheduled:'info', completed:'success', cancelled:'error', rescheduled:'warning' };
const SPEC_CHIPS = ['muhurtham','horoscope_matching','kundali_milan','nadi_astrology','vastu','numerology'];

const EMPTY_CONSULT = { consultantId:'', purposeType:'muhurtham', ceremonyId:'', consultationDate:'', consultationTime:'', duration:'', mode:'video_call', muhurthamDatetime:'', notes:'' };

export default function AstrologyPage() {
  const [tab, setTab]   = useState(0);
  const [error, setError] = useState('');
  const [consultDialog, setConsultDialog] = useState(false);
  const [editConsult, setEditConsult]     = useState(null);
  const [consultForm, setConsultForm]     = useState(EMPTY_CONSULT);
  const [filterSpec, setFilterSpec]       = useState('');
  const [confirmDel, setConfirmDel]       = useState(null);

  const { data: cData, loading: cLoad } = useQuery(ASTROLOGY_CONSULTANTS_QUERY, {
    variables: filterSpec ? { specialisations: [filterSpec] } : {},
    fetchPolicy:'cache-and-network',
  });
  const { data: myData, loading: myLoad, refetch } = useQuery(MY_ASTROLOGY_CONSULTATIONS_QUERY, { fetchPolicy:'cache-and-network' });
  const { data: cerData }   = useQuery(MY_CEREMONIES_QUERY, { fetchPolicy:'cache-and-network' });

  const consultants    = cData?.astrologyConsultants || [];
  const consultations  = myData?.myAstrologyConsultations || [];
  const ceremonies     = cerData?.myCeremonies || [];

  const [createConsult] = useMutation(CREATE_ASTROLOGY_CONSULTATION_MUTATION, { onCompleted:()=>{ setConsultDialog(false); setConsultForm(EMPTY_CONSULT); setError(''); refetch(); }, onError:e=>setError(e.message) });
  const [updateConsult] = useMutation(UPDATE_ASTROLOGY_CONSULTATION_MUTATION, { onCompleted:()=>{ setConsultDialog(false); setEditConsult(null); setError(''); refetch(); }, onError:e=>setError(e.message) });
  const [deleteConsult] = useMutation(DELETE_ASTROLOGY_CONSULTATION_MUTATION, { onCompleted:()=>{ setConfirmDel(null); refetch(); } });

  const openBook = (c) => { setEditConsult(null); setConsultForm({ ...EMPTY_CONSULT, consultantId:c.id }); setConsultDialog(true); };
  const openEdit = (c) => {
    setEditConsult(c);
    setConsultForm({ consultantId:c.consultantId||'', purposeType:c.purposeType||'muhurtham', ceremonyId:c.ceremonyId||'', consultationDate:c.consultationDate||'', consultationTime:c.consultationTime||'', duration:c.duration||'', mode:c.mode||'video_call', muhurthamDatetime:c.muhurthamDatetime||'', notes:c.notes||'' });
    setConsultDialog(true);
  };
  const handleSave = () => {
    const vars = { ...consultForm, duration:consultForm.duration?parseInt(consultForm.duration):undefined, ceremonyId: consultForm.ceremonyId || null };
    editConsult ? updateConsult({ variables:{ id:editConsult.id, ...vars } }) : createConsult({ variables:vars });
  };

  return (
    <Box sx={{ p:3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Astrology & Muhurtham</Typography>
          <Typography variant="body2" color="text.secondary">Find expert astrologers for muhurtham, horoscope matching & wedding planning consultations</Typography>
        </Box>
      </Stack>
      {error && <Alert severity="error" sx={{mb:2}} onClose={()=>setError('')}>{error}</Alert>}

      <Tabs value={tab} onChange={(_,v)=>setTab(v)} sx={{mb:3}}>
        <Tab label="Find Consultants"/>
        <Tab label={`My Consultations (${consultations.length})`}/>
      </Tabs>

      {tab===0 && (
        <Box>
          {/* Specialisation filter */}
          <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" gap={1}>
            <Chip label="All" onClick={()=>setFilterSpec('')} color={!filterSpec?'primary':'default'} variant={!filterSpec?'filled':'outlined'}/>
            {SPEC_CHIPS.map(s=>(
              <Chip key={s} label={s.replace('_',' ')} onClick={()=>setFilterSpec(s===filterSpec?'':s)}
                color={filterSpec===s?'primary':'default'} variant={filterSpec===s?'filled':'outlined'}/>
            ))}
          </Stack>
          {cLoad && <LinearProgress sx={{mb:2}}/>}
          {consultants.length===0 && !cLoad && <Typography color="text.secondary" textAlign="center" mt={4}>No consultants found.</Typography>}
          <Grid container spacing={2}>
            <Paged items={consultants}>{rows => rows.map(c=>(
              <Grid item xs={12} sm={6} md={4} key={c.id}>
                <Card variant="outlined" sx={{height:'100%'}}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box flex={1}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography fontWeight={700}>{c.name}</Typography>
                          {c.isVerified && <VerifiedIcon color="primary" sx={{fontSize:16}}/>}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">{c.city}</Typography>
                      </Box>
                      {c.isOnline && <Chip size="small" label="Online" color="success"/>}
                    </Stack>

                    <Stack direction="row" spacing={0.5} mt={1} flexWrap="wrap" gap={0.5}>
                      {(c.specialisations||[]).map(s=>(
                        <Chip key={s} size="small" label={s.replace('_',' ')} variant="outlined" color="primary"/>
                      ))}
                    </Stack>

                    {c.languagesSpoken && (
                      <Typography variant="caption" color="text.secondary" sx={{display:'block',mt:0.5}}>
                        Languages: {c.languagesSpoken.join(', ')}
                      </Typography>
                    )}
                    {c.description && <Typography variant="body2" color="text.secondary" mt={1} sx={{WebkitLineClamp:2,display:'-webkit-box',overflow:'hidden',WebkitBoxOrient:'vertical'}}>{c.description}</Typography>}
                    <Divider sx={{my:1.5}}/>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="caption" color="text.secondary">Per session</Typography>
                        <Typography variant="body2" fontWeight={600}>{c.pricePerSession!=null?`₹${Number(c.pricePerSession).toLocaleString('en-IN')}`:'On request'}</Typography>
                      </Box>
                      <Button variant="contained" size="small" onClick={()=>openBook(c)}>Book Session</Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{display:'block',mt:0.5}}>
                      Experience: {c.yearsExperience||'—'} yrs · {c.totalConsultations||0} consultations
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}</Paged>
          </Grid>
        </Box>
      )}

      {tab===1 && (
        <Box>
          {myLoad && <LinearProgress sx={{mb:2}}/>}
          {consultations.length===0 && !myLoad && <Typography color="text.secondary" textAlign="center" mt={4}>No consultations yet. Browse consultants and book a session.</Typography>}
          <Grid container spacing={2}>
            <Paged items={consultations}>{rows => rows.map(c=>(
              <Grid item xs={12} sm={6} md={4} key={c.id}>
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between">
                      <Box>
                        <Typography fontWeight={700}>{c.consultant?.name||'Unknown'}</Typography>
                        <Chip size="small" label={(c.purposeType||'').replace('_',' ')} variant="outlined" color="primary" sx={{mt:0.5}}/>
                      </Box>
                      <Chip size="small" label={c.status} color={STATUS_COLOR[c.status]||'default'}/>
                    </Stack>
                    <Stack direction="row" spacing={0.5} mt={1} alignItems="center">
                      <EventIcon sx={{fontSize:14,color:'text.secondary'}}/>
                      <Typography variant="caption" color="text.secondary">{c.consultationDate||'—'} {c.consultationTime?`· ${c.consultationTime}`:''}</Typography>
                    </Stack>
                    {c.muhurthamDatetime && (
                      <Box mt={1} p={1} sx={{bgcolor:'#e8f5e9',borderRadius:1}}>
                        <Typography variant="caption" fontWeight={700}>CONFIRMED MUHURTHAM</Typography>
                        <Typography variant="body2" fontWeight={600}>{c.muhurthamDatetime}</Typography>
                      </Box>
                    )}
                    {c.status==='completed' && c.reportUrl && (
                      <Button size="small" startIcon={<DownloadIcon/>} sx={{mt:1}} href={c.reportUrl} target="_blank">Download Report</Button>
                    )}
                    {c.notes && <Typography variant="caption" color="text.secondary" sx={{display:'block',mt:0.5,fontStyle:'italic'}}>"{c.notes}"</Typography>}
                    <Stack direction="row" spacing={0.5} mt={1}>
                      <IconButton size="small" onClick={()=>openEdit(c)}><EditIcon fontSize="small"/></IconButton>
                      <IconButton size="small" color="error" onClick={()=>setConfirmDel(c.id)}><DeleteIcon fontSize="small"/></IconButton>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}</Paged>
          </Grid>
        </Box>
      )}

      {/* Book/Edit Dialog */}
      <Dialog open={consultDialog} onClose={()=>setConsultDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editConsult?'Edit Consultation':'Book Consultation'}</DialogTitle>
        <DialogContent sx={{display:'flex',flexDirection:'column',gap:2,pt:2}}>
          <TextField select label="Consultant" value={consultForm.consultantId} onChange={e=>setConsultForm(f=>({...f,consultantId:e.target.value}))} fullWidth disabled={!!editConsult}>
            {consultants.map(c=><MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
          <TextField select label="Purpose" value={consultForm.purposeType} onChange={e=>setConsultForm(f=>({...f,purposeType:e.target.value}))} fullWidth>
            {['muhurtham','horoscope_matching','kundali_milan','nadi_astrology','vastu','numerology','general'].map(t=><MenuItem key={t} value={t}>{t.replace('_',' ')}</MenuItem>)}
          </TextField>
          <TextField select label="Ceremony" value={consultForm.ceremonyId} onChange={e=>setConsultForm(f=>({...f,ceremonyId:e.target.value}))} fullWidth>
            <MenuItem value="">— None —</MenuItem>
            {ceremonies.map(c=><MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField label="Date" type="date" value={consultForm.consultationDate} onChange={e=>setConsultForm(f=>({...f,consultationDate:e.target.value}))} fullWidth InputLabelProps={{shrink:true}}/></Grid>
            <Grid item xs={6}><TextField label="Time" type="time" value={consultForm.consultationTime} onChange={e=>setConsultForm(f=>({...f,consultationTime:e.target.value}))} fullWidth InputLabelProps={{shrink:true}}/></Grid>
          </Grid>
          <Grid container spacing={1.5}>
            <Grid item xs={6}><TextField select label="Mode" value={consultForm.mode} onChange={e=>setConsultForm(f=>({...f,mode:e.target.value}))} fullWidth>{['in_person','video_call','phone_call'].map(m=><MenuItem key={m} value={m}>{m.replace('_',' ')}</MenuItem>)}</TextField></Grid>
            <Grid item xs={6}><TextField label="Duration (mins)" type="number" value={consultForm.duration} onChange={e=>setConsultForm(f=>({...f,duration:e.target.value}))} fullWidth/></Grid>
          </Grid>
          <TextField label="Muhurtham Date & Time (if confirmed)" value={consultForm.muhurthamDatetime} onChange={e=>setConsultForm(f=>({...f,muhurthamDatetime:e.target.value}))} fullWidth placeholder="e.g. 12 Feb 2026 · 07:12 AM"/>
          <TextField label="Notes" value={consultForm.notes} onChange={e=>setConsultForm(f=>({...f,notes:e.target.value}))} multiline rows={3} fullWidth/>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setConsultDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!consultForm.consultantId}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDel} onClose={()=>setConfirmDel(null)}>
        <DialogTitle>Delete Consultation?</DialogTitle>
        <DialogActions>
          <Button onClick={()=>setConfirmDel(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={()=>deleteConsult({variables:{id:confirmDel}})}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
